import { useEffect } from "react";

/**
 * Per-route document metadata for the SPA. Upserts title, meta, canonical link
 * and a JSON-LD block into <head>, and removes the tags it created on unmount so
 * routes don't leak metadata into each other.
 */
export function useSeo({
  title,
  description,
  canonical,
  robots = "index, follow",
  og = {},
  twitterCard = "summary_large_image",
  jsonLd,
}) {
  useEffect(() => {
    const created = [];

    if (title) document.title = title;

    const upsertMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-seo", "managed");
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    };

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "twitter:card", twitterCard);
    Object.entries(og).forEach(([k, v]) => upsertMeta("property", `og:${k}`, v));

    if (canonical) {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonical);
      link.setAttribute("data-seo", "managed");
      document.head.appendChild(link);
      created.push(link);
    }

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "managed");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      created.push(script);
    }

    return () => created.forEach((el) => el.remove());
  }, [title, description, canonical, robots, twitterCard, JSON.stringify(og), JSON.stringify(jsonLd)]);
}
